import { useState, useRef, useEffect, useCallback, type RefObject } from 'react';
import { setDragBodyStyles, stopDragBodyStyles } from '../lib/dragResizeUtils';

interface UseModalDragReturn {
  modalRef: RefObject<HTMLDivElement | null>;
  position: { x: number; y: number } | null;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleDoubleClick: () => void;
}

export function useModalDrag(): UseModalDragReturn {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  const clamp = useCallback((x: number, y: number) => {
    const el = modalRef.current;
    if (!el) return { x, y };
    const rect = el.getBoundingClientRect();
    const maxX = window.innerWidth - rect.width;
    const maxY = window.innerHeight - rect.height;
    return {
      x: Math.max(0, Math.min(x, maxX)),
      y: Math.max(0, Math.min(y, maxY)),
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const el = modalRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setIsDragging(true);
    setDragBodyStyles('grabbing');
  }, []);

  const handleDoubleClick = useCallback(() => {
    setPosition(null);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      const clamped = clamp(e.clientX - offsetRef.current.x, e.clientY - offsetRef.current.y);
      setPosition(clamped);
    };

    const onMouseUp = () => {
      setIsDragging(false);
      stopDragBodyStyles();
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, clamp]);

  // Re-clamp on window resize
  useEffect(() => {
    if (!position) return;
    const onResize = () => {
      setPosition(prev => prev ? clamp(prev.x, prev.y) : null);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [position, clamp]);

  return { modalRef, position, isDragging, handleMouseDown, handleDoubleClick };
}
